/**
 * 圖片解密服務
 * 用於解密從加密 CDN 獲取的圖片資源
 */
import { config, shouldLog } from '../config/config';


/**
 * 解密結果快取
 */
const decryptedImageCache = new Map<string, string>();

/**
 * 已處理的圖片元素（避免重複處理）
 */
const processedImages = new WeakSet<HTMLImageElement>();

/**
 * MutationObserver 實例
 */
let imageObserver: MutationObserver | null = null;

/**
 * 檢查 URL 是否為加密 CDN 的圖片
 * 只有在父層有傳入 iconBaseUrl 時才啟用解密判斷
 */
function isEncryptedCdnUrl(url: string): boolean {
  if (!url) return false;

  // 父層沒有傳 iconBaseUrl，不需要解密
  if (!config.assets.isIconBaseUrlFromParent) {
    return false;
  }

  // 檢查是否為父層傳入的 iconBaseUrl
  const iconBaseUrl = config.assets.iconBaseUrl;
  if (iconBaseUrl && url.startsWith(iconBaseUrl)) {
    if (shouldLog('debug')) {
      console.log('[ImageDecrypt] 匹配加密 CDN:', url);
    }
    return true;
  }

  return false;
}

/**
 * 解密圖片
 * @param url - 加密圖片的 URL
 * @returns 解密後的 Blob URL
 */
export async function decryptImage(url: string): Promise<string> {
  if (!url) {
    throw new Error('URL is required for image decryption');
  }

  // 檢查快取
  if (decryptedImageCache.has(url)) {
    if (shouldLog('debug')) {
      console.log('[ImageDecrypt] 使用快取:', url);
    }
    return decryptedImageCache.get(url)!;
  }

  try {
    if (shouldLog('debug')) {
      console.log('[ImageDecrypt] 開始解密:', url);
    }

    // 獲取加密圖片
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch encrypted image: ${response.status}`);
    }

    // 取得加密資料和參數
    const encryptedData = await response.arrayBuffer();
    const initializationVector = response.headers.get('x-encrypted-iv');
    const encryptionAlgorithm = response.headers.get('x-encrypted-algo');
    const encryptionKeyId = response.headers.get('x-encrypted-key-id');
    const contentType = response.headers.get('x-content-type');

    // 檢查是否為加密圖片（如果沒有加密 headers，直接返回原始 URL）
    if (!initializationVector || !encryptionAlgorithm || !encryptionKeyId) {
      if (shouldLog('debug')) {
        console.log('[ImageDecrypt] 非加密圖片，直接返回原始 URL');
      }
      decryptedImageCache.set(url, url);
      return url;
    }

    // 檢查金鑰是否存在（從 config 取得父層傳入的金鑰）
    const keyValue = config.assets.encryptKeyMap.get(encryptionKeyId);
    if (!keyValue) {
      throw new Error(`Unknown encryption key ID: ${encryptionKeyId}`);
    }

    // 使用 Web Crypto API 解密
    const subtleCrypto = window.crypto.subtle || (window.crypto as unknown as { webkitSubtle: SubtleCrypto }).webkitSubtle;
    if (!subtleCrypto) {
      throw new Error('瀏覽器不支援 Web Crypto API');
    }

    // 匯入解密金鑰
    const cryptoKey = await subtleCrypto.importKey(
      'raw',
      Uint8Array.from(atob(keyValue), (c) => c.charCodeAt(0)),
      encryptionAlgorithm,
      false,
      ['decrypt']
    );

    // 執行解密
    const buffer = await subtleCrypto.decrypt(
      {
        name: encryptionAlgorithm,
        iv: Uint8Array.from(atob(initializationVector), (c) => c.charCodeAt(0)),
      },
      cryptoKey,
      encryptedData
    );

    // 轉換為 Blob URL
    const blob = new Blob([buffer], { type: contentType || 'image/jpeg' });
    const blobUrl = URL.createObjectURL(blob);

    // 快取結果
    decryptedImageCache.set(url, blobUrl);

    if (shouldLog('debug')) {
      console.log('[ImageDecrypt] 解密成功:', url);
    }

    return blobUrl;
  } catch (error) {
    if (shouldLog('error')) {
      console.error('[ImageDecrypt] 解密失敗:', error);
    }

    // 根據錯誤類型提供更詳細的資訊
    if (error instanceof Error) {
      if (error.message.includes('Failed to fetch')) {
        throw new Error('無法獲取加密圖片，可能是網路問題或 URL 錯誤');
      } else if (error.name === 'OperationError') {
        throw new Error('解密失敗，可能是金鑰或 IV 不正確');
      }
    }

    throw error;
  }
}

/**
 * 安全解密圖片（失敗時返回 null）
 * @param url - 加密圖片的 URL
 * @returns 解密後的 Blob URL 或 null
 */
export async function decryptImageSafely(url: string): Promise<string | null> {
  try {
    return await decryptImage(url);
  } catch (error) {
    if (shouldLog('warn')) {
      console.warn('[ImageDecrypt] 解密失敗，返回 null:', error);
    }
    return null;
  }
}

/**
 * 批次解密多張圖片（並行處理）
 * @param urls - 加密圖片的 URL 陣列
 * @returns 解密後的 Blob URL 陣列（失敗的位置為 null）
 */
export async function decryptImages(urls: string[]): Promise<(string | null)[]> {
  return Promise.all(urls.map((url) => decryptImageSafely(url)));
}

/**
 * 釋放 Blob URL 記憶體
 * @param blobUrl - 要釋放的 Blob URL
 */
export function revokeBlobUrl(blobUrl: string): void {
  if (blobUrl && blobUrl.startsWith('blob:')) {
    URL.revokeObjectURL(blobUrl);

    // 從快取中移除
    for (const [key, value] of decryptedImageCache.entries()) {
      if (value === blobUrl) {
        decryptedImageCache.delete(key);
        break;
      }
    }
  }
}

/**
 * 清除所有快取的 Blob URL
 */
export function clearDecryptedImageCache(): void {
  for (const blobUrl of decryptedImageCache.values()) {
    if (blobUrl.startsWith('blob:')) {
      URL.revokeObjectURL(blobUrl);
    }
  }
  decryptedImageCache.clear();

  if (shouldLog('debug')) {
    console.log('[ImageDecrypt] 快取已清除');
  }
}

/**
 * 建構加密 CDN 圖片 URL
 * @param encCdnBaseUrl - 加密 CDN 基礎 URL
 * @param path - 圖片路徑
 * @param options - 圖片選項
 */
export function buildEncryptedImageUrl(
  encCdnBaseUrl: string,
  path: string,
  options?: {
    format?: 'jpg' | 'png' | 'webp';
    size?: string;
  }
): string {
  const url = new URL(path, encCdnBaseUrl);

  if (options?.format) {
    url.searchParams.set('format', options.format);
  }
  if (options?.size) {
    url.searchParams.set('size', options.size);
  }

  return url.href;
}

/**
 * 處理單一圖片元素的解密
 */
async function processImageElement(img: HTMLImageElement): Promise<void> {
  // 已經處理過的圖片跳過
  if (processedImages.has(img)) return;

  const originalSrc = img.getAttribute('src') || '';

  // 檢查是否為加密 CDN 的圖片
  if (!isEncryptedCdnUrl(originalSrc)) return;

  // 標記為已處理
  processedImages.add(img);

  // 儲存原始的 onerror handler 並暫時移除，避免加密資料觸發錯誤
  const originalOnerror = img.onerror;
  img.onerror = null;

  // 儲存原始的加密 URL（用於解密失敗時的 data 屬性）
  if (!img.dataset.encryptedSrc) {
    img.dataset.encryptedSrc = originalSrc;
  }

  try {
    const decryptedUrl = await decryptImage(originalSrc);
    img.src = decryptedUrl;

    // 解密成功後，標記父容器為已解密（移除骨架圖 loading）
    const container = img.closest('.reward-icon-container');
    if (container) {
      container.classList.add('loaded');
    }

    // 解密成功後，重置可能被 onerror 設置的 display 樣式
    if (img.style.display === 'none') {
      img.style.display = '';
    }

    if (shouldLog('debug')) {
      console.log('[ImageDecrypt] 圖片已解密:', originalSrc);
    }
  } catch (error) {
    // 解密失敗，也標記為已處理（移除骨架圖 loading）
    const container = img.closest('.reward-icon-container');
    if (container) {
      container.classList.add('loaded');
    }

    // 解密失敗，恢復 onerror handler 並觸發錯誤處理
    img.onerror = originalOnerror;
    if (typeof originalOnerror === 'function') {
      originalOnerror.call(img, new Event('error'));
    }

    if (shouldLog('warn')) {
      console.warn('[ImageDecrypt] 圖片解密失敗:', originalSrc, error);
    }
  }
}

/**
 * 處理 DOM 中所有的圖片
 */
function processAllImages(): void {
  const images = document.querySelectorAll<HTMLImageElement>('img[src]');
  images.forEach((img) => {
    processImageElement(img);
  });
}

/**
 * 啟動圖片自動解密服務
 * 會觀察 DOM 變化，自動解密新加入的圖片
 */
export function startImageDecryptService(): void {
  if (imageObserver) {
    if (shouldLog('debug')) {
      console.log('[ImageDecrypt] 服務已經啟動');
    }
    return;
  }

  if (shouldLog('info')) {
    console.log('[ImageDecrypt] 啟動圖片解密服務');
  }

  // 處理現有的圖片
  processAllImages();

  // 建立 MutationObserver 監聽新加入的圖片
  imageObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      // 只處理新增的節點，不處理 src 屬性變化（避免無限循環）
      mutation.addedNodes.forEach((node) => {
        if (node instanceof HTMLImageElement) {
          processImageElement(node);
        } else if (node instanceof HTMLElement) {
          // 遞迴處理子節點中的圖片
          const images = node.querySelectorAll<HTMLImageElement>('img[src]');
          images.forEach((img) => processImageElement(img));
        }
      });

      // 注意：不再處理 src 屬性變化，因為會導致無限循環：
      // processImageElement 設置 img.src -> 觸發 MutationObserver -> 重新處理 -> 無限循環
    });
  });

  // 開始觀察（只監聽 childList，不監聽 attributes）
  imageObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
}

/**
 * 停止圖片自動解密服務
 */
export function stopImageDecryptService(): void {
  if (imageObserver) {
    imageObserver.disconnect();
    imageObserver = null;

    if (shouldLog('info')) {
      console.log('[ImageDecrypt] 圖片解密服務已停止');
    }
  }
}
